import { StyleSheet, ViewProps } from 'react-native'
import { useEffect, useRef } from 'react'
import { Draft, ProfileData, useStore } from 'zens-store'
import { observer } from 'mobx-react-lite'
import { Trans, useTranslation } from 'react-i18next'
import { object, string } from 'yup'
import { Color, Font, Spacing, Text } from 'zens-storybook'

import { Form } from '../form'
import { NavigationConfirm } from '../navigation'
import { UserLanguage } from '../user'

import ProfileChange from './change'

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  intro: {
    color: Color.metalGrey,
    marginBottom: Spacing.M,
  },
  change: {
    marginTop: -Spacing.L,
  },
  changeField: {
    maxWidth: undefined,
  },
  highlight: {
    fontFamily: Font.medium,
  },
})

interface Props extends ViewProps {
  data: ProfileData
  onSave?: (draft: Draft<ProfileData>) => Promise<void>
  onCancel?: (draft: Draft<ProfileData>) => Promise<void>
  onDelete?: (draft: Draft<ProfileData>) => Promise<void>
  onEmail?: () => void
  onPassword?: () => void
  errors?: Record<keyof ProfileData, string>
}

const ProfileForm = (props: Props) => {
  const { data, onSave, onCancel, onEmail, onPassword, onDelete, style, ...otherProps } = props
  const { t } = useTranslation()
  const { i18n } = useStore()
  const draft = useRef(new Draft({ ...data, language: data.language || i18n.language })).current

  useEffect(() => {
    draft.update('email', data.email)
    draft.settle('email')
  }, [data.email])

  const { email } = draft.data

  const config = {
    groups: [
      {
        fields: [
          {
            key: 'name',
            label: t('Full name'),
          },
          {
            key: 'language',
            label: t('Language'),
            FieldInputComponent: UserLanguage,
            fieldProps: {
              testID: 'ProfileForm.Language',
            },
          },
        ],
      },
      {
        fields: [
          {
            key: 'email',
            label: t('Email address'),
            FieldInputComponent: ProfileChange,
            fieldProps: {
              style: styles.changeField,
              text: (
                <Text>
                  <Trans>
                    {/* @ts-expect-error Typing of vars in trans not working correctly  */}
                    Your email address is <Text style={styles.highlight}>{{ email }}</Text>
                  </Trans>
                </Text>
              ),
              onChange: onEmail,
            },
            style: styles.change,
          },
        ],
      },
      {
        fields: [
          {
            key: 'password',
            label: t('Password'),
            FieldInputComponent: ProfileChange,
            fieldProps: {
              style: styles.changeField,
              onChange: onPassword,
            },
            style: styles.change,
          },
        ],
      },
    ],
  }

  const schema = object({
    name: string().required(),
  })
  return (
    <>
      <Text style={styles.intro}>
        <Trans>These preferences only apply to you.</Trans>
      </Text>
      <Form
        style={[styles.container, style]}
        draft={draft}
        config={config}
        schema={schema}
        buttons={[
          [
            {
              title: t('Save'),
              onPress: onSave,
              validate: true,
            },
            {
              title: t('Cancel'),
              onPress: onCancel,
              light: true,
            },
          ],
          ...(onDelete
            ? [
                {
                  title: t('Delete my user profile'),
                  onPress: onDelete,
                  left: 'trash' as const,
                  danger: true,
                  testID: 'ProfileForm.Delete',
                },
              ]
            : []),
        ]}
        {...otherProps}
      />
      <NavigationConfirm draft={draft} />
    </>
  )
}

export default observer(ProfileForm)
